export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const ZERO_INSETS: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };

let probeEl: HTMLDivElement | null = null;

/**
 * PR31 (landscape safe areas). `env(safe-area-inset-*)` (from
 * `index.html`'s existing `viewport-fit=cover`) is CSS-only - there is no
 * direct JS API for it. The standard technique (used here) is to read it
 * back off a hidden probe element's COMPUTED padding, since `env()` values
 * resolve to real pixel lengths once applied to any CSS property and can
 * then be read via `getComputedStyle()`. The probe is created once and
 * reused (never re-created per call) - safe-area insets only change on
 * orientation change/device rotation, which already re-triggers a full
 * layout pass (`GameScene.onResize()`) that re-reads this.
 *
 * Returns all-zero on any non-browser environment (this project's Vitest
 * suite runs in plain Node - see `vitest.config.ts`) or on a browser that
 * doesn't support `env()` (the `, 0px` fallback in each probe property
 * already handles that natively - this defensive check is only for
 * `document` being entirely absent).
 */
export function readSafeAreaInsets(): SafeAreaInsets {
  if (typeof document === "undefined") return ZERO_INSETS;

  if (!probeEl) {
    const el = document.createElement("div");
    el.style.position = "fixed";
    el.style.top = "0";
    el.style.left = "0";
    el.style.visibility = "hidden";
    el.style.pointerEvents = "none";
    el.style.paddingTop = "env(safe-area-inset-top, 0px)";
    el.style.paddingRight = "env(safe-area-inset-right, 0px)";
    el.style.paddingBottom = "env(safe-area-inset-bottom, 0px)";
    el.style.paddingLeft = "env(safe-area-inset-left, 0px)";
    document.body.appendChild(el);
    probeEl = el;
  }

  const style = getComputedStyle(probeEl);
  return {
    top: parseFloat(style.paddingTop) || 0,
    right: parseFloat(style.paddingRight) || 0,
    bottom: parseFloat(style.paddingBottom) || 0,
    left: parseFloat(style.paddingLeft) || 0,
  };
}
